<script lang="ts" setup>
import {
	openErrorsModal,
	openGlobalConfirmModal,
} from "@/components/modals/modalDefinitions";
import { filterArray, flatten } from "@/assets/js/helpers";
import { useDefaultStore } from "@/store";
import { useSessionStore } from "@/store/session";
import type { TCategory, TClients, TRole } from "@/assets/js/globalTypes";
import { useToastStore } from "@/store/toast";

const store = useDefaultStore();
const sessionStore = useSessionStore();
const updateDocumentInfoMsg = ref("");
const updateDocumentInfoErrorMsg = ref("");
const updateApplicableWardsInfoMsg = ref("");
const updateApplicableWardsErrorMsg = ref("");
const updateCategoriesInfoMsg = ref("");
const updateCategoriesErrorMsg = ref("");
const updateRolesErrorMsg = ref("");
const updateRolesInfoMsg = ref("");
const documentsErrors = computed(() => store.getDocumentsErrors());
const isLoadingInfo = ref(false);
const isLoadingSaveNewDocument = ref();
const toastStore = useToastStore();

const canEdit = computed(() => {
	return sessionStore.can("edit-document");
});

type TDocumentDetails = {
	id: number;
	name?: string;
	optional?: boolean;
	info_link?: string;
	has_expiry?: boolean;
	notification_deadline?: number | null;
	expiration_length?: number | null;
	locationCount?: number;
	locations?: any[];
	categoryCount?: number;
	categories?: any[];
	roles?: any[];
};

const tempDocInfo = ref<TDocumentDetails>({
	id: -1,
	name: "",
	optional: true,
	info_link: "",
	has_expiry: false,
	notification_deadline: null,
	expiration_length: null,
	locationCount: 0,
	locations: [],
	categoryCount: 0,
	categories: [],
	roles: [],
});

const props = withDefaults(
	defineProps<{
		document?: any | null;
		clients?: TClients[];
		categories?: TCategory[];
		roles?: TRole[];
	}>(),
	{ document: null, clients: () => [], categories: () => [], roles: () => [] },
);

const emit = defineEmits(["close-modal"]);

const expandedSections = ref<Record<string, boolean>>({
	info: true,
	wards: false,
	categories: false,
	visibility: false,
});

function toggleSection(section: keyof typeof expandedSections.value) {
	expandedSections.value[section] = !expandedSections.value[section];

	if (!expandedSections.value[section]) return;

	switch (section) {
		case "wards": {
			editLocationsLogic.showLocations(tempDocInfo.value);

			break;
		}
		case "categories": {
			editSubcategoriesLogic.showSubcategories(tempDocInfo.value);

			break;
		}
		case "visibility": {
			editRolesLogic.showRoles(tempDocInfo.value);

			break;
		}
	}
}

function clearErrorMessages() {
	updateDocumentInfoMsg.value = "";
	updateDocumentInfoErrorMsg.value = "";

	updateApplicableWardsInfoMsg.value = "";
	updateApplicableWardsErrorMsg.value = "";

	updateCategoriesInfoMsg.value = "";
	updateCategoriesErrorMsg.value = "";

	updateRolesErrorMsg.value = "";
	updateRolesInfoMsg.value = "";
}

onBeforeUnmount(() => {
	setTempDocumentInfo();
	clearErrorMessages();
});

function setTempDocumentInfo(document: any = {}) {
	tempDocInfo.value = {
		id: document?.id ?? 0,
		name: document?.name ?? "",
		optional: document?.optional ?? true,
		info_link: document?.info_link ?? "",
		has_expiry: document?.expiration_months > 0 ? true : false,
		expiration_length: document?.expiration_months ?? null,
		notification_deadline: document?.notification_days ?? null,
		locationCount: document?.locationCount ?? 0,
		locations: document?.locations ?? [],
		categoryCount: document?.categoryCount ?? 0,
		categories: document?.categories ?? [],
		roles: document?.roles ?? [],
	};
}

// info start

const isNotOptional = computed({
	get: () => !tempDocInfo.value.optional,
	set: (val) => (tempDocInfo.value.optional = !val),
});

async function onClickEditInfoConfirm() {
	if (!props.document) return;

	isLoadingInfo.value = true;

	const params: Partial<TDocumentDetails> = {
		id: tempDocInfo.value.id,
	};

	if (
		tempDocInfo.value.name &&
		tempDocInfo.value.name !== props.document.name
	) {
		params.name = tempDocInfo.value.name;
	}
	if (tempDocInfo.value.optional !== props.document.optional) {
		params.optional = tempDocInfo.value.optional;
	}
	if (tempDocInfo.value.info_link !== props.document.info_link) {
		params.info_link = tempDocInfo.value.info_link;
	}
	const docHasExpiry = props.document.expiration_months > 0;
	if (tempDocInfo.value.has_expiry !== docHasExpiry) {
		if (
			tempDocInfo.value.expiration_length &&
			tempDocInfo.value.notification_deadline
		) {
			params.expiration_length = tempDocInfo.value.expiration_length;
			params.notification_deadline = tempDocInfo.value.notification_deadline;
		} else {
			params.expiration_length = null;
			params.notification_deadline = null;
		}
	}

	const res = await store.updateDocument({
		...params,
	});

	const updatedDocument = res.data.data;

	if (documentsErrors.value) {
		updateDocumentInfoErrorMsg.value = documentsErrors.value;
		store.resetDocumentsErrors();
	} else {
		applyDocumentChanges(updatedDocument);
		updateDocumentInfoMsg.value = "Document Updated.";
	}

	isLoadingInfo.value = false;
}

const hasChangesInfo = computed(() => {
	if (!props.document) return false;

	const info = tempDocInfo.value;
	const doc = props.document;

	const docHasExpiry = doc.expiration_months > 0;

	const basicChanges =
		(info.name && info.name !== doc.name) ||
		info.optional !== doc.optional ||
		info.info_link !== doc.info_link ||
		info.has_expiry !== docHasExpiry ||
		info.expiration_length !== doc.expiration_months ||
		info.notification_deadline !== doc.notification_days;

	// if expiry is enabled, require both expiration_length and notification_deadline
	const expiryIncomplete =
		info.has_expiry && (!info.expiration_length || !info.notification_deadline);

	return expiryIncomplete ? false : basicChanges;
});

function applyDocumentChanges(updatedDocument) {
	const documents = computed(() => store.getDocuments());

	const index = documents.value.findIndex(
		(d) =>
			d.id === updatedDocument.id && d?.temp?.id === updatedDocument?.temp?.id,
	);
	if (index !== -1) Object.assign(documents.value[index], updatedDocument);
	store.setDocuments(documents.value);
}

watch(
	() => tempDocInfo.value.has_expiry,
	(newVal) => {
		if (props.document) {
			if (newVal) {
				tempDocInfo.value.expiration_length = props.document.expiration_months;
				tempDocInfo.value.notification_deadline =
					props.document.notification_days;
			} else {
				tempDocInfo.value.expiration_length = null;
				tempDocInfo.value.notification_deadline = null;
			}
		}
	},
);

// info end

// locations start

const canViewAllowances = store.initialStatus.can_view_allowances;
const isSavingWards = ref(false);
const documentLocations = ref<any[]>([]);
const allClients = ref<TClients[]>([]);

const wardsObj = ref({
	show: false,
	displayCaret: true,
	data: [],
	levels: [],
	save: () => editLocationsLogic.onSaveChanges(),
	isLoading: false,
	search: {
		placeholder: "Filter Wards",
		value: "",
		addTimeout: false,
	},
	checkbox: {
		label: "Wards",
		checkedCount: true,
		show: false,
	},
});

const sectors = computed(() => [
	{
		name: "",
		id: 1,
		clients: allClients.value,
	},
]);

const editLocationsLogic = {
	apiCheckedIds: [],
	clientLocations: [],
	apiCheckedLocationAllowanceIds: [],
	showLocations: async (row: any) => {
		wardsObj.value.isLoading = true;
		editLocationsLogic.apiCheckedIds = [];
		editLocationsLogic.apiCheckedLocationAllowanceIds = [];
		if (allClients.value.length === 0) {
			await store.fetchClientsList({
				includes: ["locations"],
				per_page: 999,
				optimise: true,
			});
			allClients.value = store.getAllClients();
		}
		if (props.document) {
			await store.fetchDocumentLocations(row);
			documentLocations.value = store.getDocumentWithLocations();
		} else {
			documentLocations.value = tempDocInfo.value.locations;
		}
		editLocationsLogic.apiCheckedIds = editLocationsLogic.findCheckedItems(
			documentLocations.value,
		);
		const locationAllowanceMap = editLocationsLogic.getLocationAllowanceMap(
			documentLocations.value,
		);
		const checkedIdObjects = editLocationsLogic.getCheckedCCs(
			sectors.value,
			editLocationsLogic.apiCheckedIds,
			locationAllowanceMap,
		);
		editLocationsLogic.clientLocations =
			editLocationsLogic.packLocationsData(checkedIdObjects);
		wardsObj.value.data = editLocationsLogic.clientLocations;
		wardsObj.value.levels = ["locations"];
		wardsObj.value.search.value = "";
		wardsObj.value.displayCaret = true;
		wardsObj.value.checkbox.show = true;
		wardsObj.value.checkbox.checkedCount = true;
		wardsObj.value.isLoading = false;

		if (documentsErrors.value) {
			openErrorsModal({
				content: documentsErrors.value,
			});
			store.resetDocumentsErrors();
		}
		wardsObj.value.show = true;
	},
	findCheckedItems: (row: any) => {
		const checkedItems = [];
		row?.map((location) => {
			checkedItems.push(location["id"]);
		});
		return checkedItems;
	},
	getLocationAllowanceMap(documentLocations: any): any {
		const locationAllowanceMap = {};

		for (const location of documentLocations) {
			locationAllowanceMap[location.id] = Boolean(
				location.hasComplianceAllowance,
			);
			if (Boolean(location.hasComplianceAllowance))
				editLocationsLogic.apiCheckedLocationAllowanceIds.push(location.id);
		}
		return locationAllowanceMap;
	},
	getCheckedCCs(sectors: any, ids: any[] = [], locationAllowanceMap?: any) {
		const updatedSectors = [];
		for (const sector of sectors) {
			for (let j = 0; j < sector.clients.length; j++) {
				const client = sector.clients[j];
				for (let k = 0; k < client.locations.length; k++) {
					const location = client.locations[k];
					const locId = location.id;
					location.checked = ids.includes(locId);
					location.hasComplianceAllowance =
						locationAllowanceMap?.[locId] ?? false;
				}
			}
			updatedSectors.push(sector);
		}
		return updatedSectors;
	},
	packLocationsData(sectors: any[]): any[] {
		const packed: any[] = [];
		for (const sector of sectors) {
			for (const client of sector.clients) {
				client.locations.sort(
					(a: any, b: any) => Number(b.checked) - Number(a.checked),
				);
				packed.push(client);
			}
		}

		packed.sort((a, b) => {
			const aChecked = a.locations?.some((loc: any) => loc.checked) ? 1 : 0;
			const bChecked = b.locations?.some((loc: any) => loc.checked) ? 1 : 0;
			return bChecked - aChecked;
		});

		return packed;
	},
	getCheckedLocationsByClientsByCheckedIds(clients: any[], ids: any[]) {
		for (const client of clients) {
			for (let j = 0; j < client.locations.length; j++) {
				const loc = client.locations[j];
				loc.checked = ids.includes(loc.id);
				loc.hasComplianceAllowance =
					editLocationsLogic.apiCheckedLocationAllowanceIds.includes(loc.id)
						? true
						: false;
			}
		}
		return clients;
	},
	updateSearchValue(val: string): void {
		clearErrorMessages();
		wardsObj.value.search.value = val;
		editLocationsLogic.clientLocations =
			editLocationsLogic.getCheckedLocationsByClientsByCheckedIds(
				editLocationsLogic.clientLocations,
				editLocationsLogic.apiCheckedIds,
			);
		const filtered = filterArray(
			editLocationsLogic.clientLocations,
			val,
			"locations",
		);
		wardsObj.value.data = filtered;
	},
	selectAllCheckbox(val: boolean): void {
		clearErrorMessages();
		wardsObj.value.checkbox.checkedCount = true;
		for (const location of wardsObj.value.data) {
			location.checked = val;
			editLocationsLogic.apiCheckedIds.push(location.id);
		}
	},
	async onSaveChanges(): Promise<void> {
		const ids = editLocationsLogic.clientLocations.map((client) => {
			return client.locations
				.filter((cc) => {
					if (editLocationsLogic.apiCheckedIds.includes(cc.id)) {
						cc.hasComplianceAllowance =
							editLocationsLogic.apiCheckedLocationAllowanceIds.includes(cc.id)
								? true
								: false;
						return cc.id;
					}
				})
				.map((cc) => ({
					id: cc.id,
					hasAllowance: cc.hasComplianceAllowance,
				}));
		});
		const centres = props.document
			? flatten(ids).map((o) => ({
					id: o.id,
					has_allowance: o.hasAllowance,
				}))
			: flatten(ids).map((o) => ({
					id: o.id,
					has_allowance: o.hasAllowance,
					hasComplianceAllowance: o.hasAllowance,
				}));

		if (props.document) {
			isSavingWards.value = true;
			const res = await store.updateDocument({
				id: props.document.id,
				locations: centres,
			});

			const updatedDocument = res.data.data;

			if (documentsErrors.value) {
				updateApplicableWardsErrorMsg.value = documentsErrors.value;
				store.resetDocumentsErrors();
			} else {
				applyDocumentChanges(updatedDocument);
				updateApplicableWardsInfoMsg.value = "Document Updated.";
			}

			isSavingWards.value = false;
		} else {
			tempDocInfo.value.locations = centres;
			tempDocInfo.value.locationCount = centres.length;
		}
	},
	selectAll(isAllCheckboxSelected) {
		clearErrorMessages();
		if (isAllCheckboxSelected) {
			wardsObj.value.data.map((client) => {
				if (client?.locations && client.locations.length > 0) {
					client.locations.map((loc) => {
						editLocationsLogic.apiCheckedIds.push(loc.id);
					});
				}
			});
		} else {
			editLocationsLogic.apiCheckedIds = [];
		}
	},
};

// locations end

// categories start

const isSavingCategories = ref<boolean>(false);
const allCategories = ref<TCategory[]>([]);

const catObj = ref({
	show: false,
	displayCaret: true,
	data: [],
	levels: [],
	save: () => editSubcategoriesLogic.onSaveChanges(),
	isLoading: false,
	search: {
		placeholder: "Filter Categories",
		value: "",
		addTimeout: false,
	},
	checkbox: {
		label: "Categories",
		checkedCount: true,
		show: false,
	},
});

const editSubcategoriesLogic = {
	apiCheckedIds: [],
	showSubcategories: async (row: any) => {
		catObj.value.isLoading = true;
		editSubcategoriesLogic.apiCheckedIds = [];
		if (allCategories.value.length === 0) {
			await store.fetchAllCategoriesList();
			if (documentsErrors.value) {
				openErrorsModal({
					content: "Failed to fetch categories.",
				});
				await store.resetDocumentsErrors();
			}
			allCategories.value = store.allCategories;
		}
		editSubcategoriesLogic.apiCheckedIds =
			editSubcategoriesLogic.findCheckedItems(row);
		catObj.value.data = editSubcategoriesLogic.getSubcategoriesByCategories(
			allCategories.value,
			editSubcategoriesLogic.apiCheckedIds,
		);
		catObj.value.levels = ["subcategories"];
		catObj.value.search.value = "";
		catObj.value.displayCaret = true;
		catObj.value.checkbox.show = true;
		catObj.value.checkbox.checkedCount = true;
		catObj.value.isLoading = false;

		if (documentsErrors.value) {
			openErrorsModal({
				content: documentsErrors.value,
			});
			store.resetDocumentsErrors();
		}
		catObj.value.show = true;
	},
	findCheckedItems: (row: any) => {
		const checkedItems = [];
		row["categories"]?.map((subcategory) => {
			checkedItems.push(subcategory["id"]);
		});
		return checkedItems;
	},
	getSubcategoriesByCategories(categories: any[], ids: any[]) {
		for (const category of categories) {
			for (let j = 0; j < category.subcategories.length; j++) {
				const subcat = category.subcategories[j];
				subcat.checked = ids.includes(subcat.id);
			}
			// sort subcategories – checked ones first
			category.subcategories.sort(
				(a: any, b: any) => Number(b.checked) - Number(a.checked),
			);
		}
		// sort categories – those with any checked subcategory first
		categories.sort((a, b) => {
			const aChecked = a.subcategories?.some((s: any) => s.checked) ? 1 : 0;
			const bChecked = b.subcategories?.some((s: any) => s.checked) ? 1 : 0;
			return bChecked - aChecked;
		});

		return categories;
	},
	updateSearchValue(val: string): void {
		clearErrorMessages();
		catObj.value.search.value = val;
		if (catObj.value.levels[0] === "subcategories") {
			const filtered = filterArray(allCategories.value, val, "subcategories");
			catObj.value.data = editSubcategoriesLogic.getSubcategoriesByCategories(
				filtered,
				editSubcategoriesLogic.apiCheckedIds,
			);
		}
	},
	selectAllCheckbox(val: boolean): void {
		clearErrorMessages();
		catObj.value.checkbox.checkedCount = true;
		for (const category of catObj.value.data) {
			for (const subcat of category.subcategories) {
				subcat.checked = val;
				editSubcategoriesLogic.apiCheckedIds.push(subcat.id);
			}
		}
	},
	async onSaveChanges(): Promise<void> {
		const ids = editSubcategoriesLogic
			.getSubcategoriesByCategories(
				allCategories.value,
				editSubcategoriesLogic.apiCheckedIds,
			)
			.map((category) => {
				return category.subcategories
					.filter((subcat) => {
						if (editSubcategoriesLogic.apiCheckedIds.includes(subcat.id)) {
							return subcat.id;
						}
					})
					.map((subcat) => subcat.id);
			});
		const categoriesFlatten = flatten(ids).map((id) => ({ id: id }));

		if (props.document) {
			isSavingCategories.value = true;
			const res = await store.updateDocument({
				id: props.document.id,
				categories: categoriesFlatten,
			});

			const updatedDocument = res.data.data;

			if (documentsErrors.value) {
				updateCategoriesErrorMsg.value = documentsErrors.value;
				store.resetDocumentsErrors();
			} else {
				applyDocumentChanges(updatedDocument);
				tempDocInfo.value.categories = updatedDocument?.categories;
				updateCategoriesInfoMsg.value = "Document Updated.";
			}

			isSavingCategories.value = false;
		} else {
			tempDocInfo.value.categories = categoriesFlatten;
			tempDocInfo.value.categoryCount = categoriesFlatten.length;
		}
	},
	selectAll(isAllCheckboxSelected) {
		clearErrorMessages();
		if (isAllCheckboxSelected) {
			catObj.value.data.map((cat) => {
				if (cat?.subcategories && cat.subcategories.length > 0) {
					cat.subcategories.map((subcat) => {
						editSubcategoriesLogic.apiCheckedIds.push(subcat.id);
					});
				}
			});
		} else {
			editSubcategoriesLogic.apiCheckedIds = [];
		}
	},
};

// categories end

// roles start

const allRoles = ref<TRole[]>([]);
const isSavingRoles = ref(false);

const rolesObj = ref({
	show: false,
	displayCaret: true,
	data: [],
	levels: [],
	save: (data: any) => editRolesLogic.onSaveChanges(data),
	isLoading: false,
	checkbox: {
		label: "Roles",
		checkedCount: true,
		show: false,
	},
});

const packedRoles = computed(() => [
	{
		name: "Roles",
		id: 1,
		roles: allRoles.value,
	},
]);

const editRolesLogic = {
	apiCheckedIds: [],
	documentRoles: [],
	showRoles: async (row: any) => {
		rolesObj.value.isLoading = true;
		editRolesLogic.apiCheckedIds = [];
		if (allRoles.value.length === 0) {
			await store.fetchAllRolesList();
			if (documentsErrors.value) {
				openErrorsModal({
					content: "Failed to fetch roles.",
				});
				await store.resetDocumentsErrors();
			}
			allRoles.value = store.allRoles;
		}
		editRolesLogic.apiCheckedIds = editRolesLogic.findCheckedItems(row);
		editRolesLogic.documentRoles = editRolesLogic.packRoles(
			packedRoles.value,
			editRolesLogic.apiCheckedIds,
		);
		rolesObj.value.data = editRolesLogic.documentRoles;
		rolesObj.value.levels = ["roles"];
		rolesObj.value.displayCaret = false;
		rolesObj.value.checkbox.show = false;
		rolesObj.value.checkbox.checkedCount = true;
		rolesObj.value.isLoading = false;

		if (documentsErrors.value) {
			openErrorsModal({
				content: documentsErrors.value,
			});
			store.resetDocumentsErrors();
		}
		rolesObj.value.show = true;
	},
	findCheckedItems: (row: any) => {
		const checkedItems = [];
		row["roles"].map((role) => {
			checkedItems.push(role["id"]);
		});
		return checkedItems;
	},
	packRoles(roles: any[], ids: any[]): any {
		for (const role of roles) {
			for (const r of role.roles) {
				r.checked = ids.includes(r.id);
			}
		}

		return roles;
	},
	async onSaveChanges({
		data,
	}: { data?: any; entity?: any } = {}): Promise<void> {
		if (props.document) {
			const ids = data[0].roles
				.filter((role) => {
					if (role.checked === true) return role.id;
				})
				.map((role) => role.id);

			// prepare data for api
			const rolesFlat = flatten(ids).map((id) => ({ id: id }));

			isSavingRoles.value = true;
			const res = await store.updateDocument({
				id: props.document.id,
				roles: rolesFlat,
			});
			const updatedDocument = res.data.data;

			if (documentsErrors.value) {
				updateRolesErrorMsg.value = documentsErrors.value;
				store.resetDocumentsErrors();
			} else {
				applyDocumentChanges(updatedDocument);

				const updatedRoles = editRolesLogic.getRolesByCheckedIds(
					allRoles.value,
					editRolesLogic.apiCheckedIds,
				);
				const ids = updatedRoles
					.filter((role) => {
						if (role.checked === true) return role.id;
					})
					.map((role) => role.id);
				const rolesFlat = flatten(ids).map((id) => ({ id: id }));
				tempDocInfo.value.roles = rolesFlat;

				updateRolesInfoMsg.value = "Document Updated.";
			}

			isSavingRoles.value = false;
		} else {
			const data = editRolesLogic.getRolesByCheckedIds(
				allRoles.value,
				editRolesLogic.apiCheckedIds,
			);

			const ids = data
				.filter((role) => {
					if (role.checked === true) return role.id;
				})
				.map((role) => role.id);

			// prepare data for api
			const rolesFlat = flatten(ids).map((id) => ({ id: id }));

			tempDocInfo.value.roles = rolesFlat;
		}
	},
	getRolesByCheckedIds(allRoles: any[], checkedIds: any[]) {
		for (const role of allRoles) {
			role.checked = checkedIds.includes(role.id);
		}
		// roleGroup.roles.sort(
		// 	(a: any, b: any) => Number(b.checked) - Number(a.checked),
		// );
		return allRoles;
	},
};

// roles end

function checkedEl(el, logicObj) {
	clearErrorMessages();
	if (logicObj.apiCheckedIds.includes(el.id) && !el.checked) return;
	/**
	 * // TODO: refactor or recreate component EditDocumentDetails
	 * el.checked is always late due to created logic in EditDocumentDetails
	 * onClickInputRow() is called before checked is being set
	 * that's why logic is opposite
	 */ else if (!logicObj.apiCheckedIds.includes(el.id) && !el.checked) {
		logicObj.apiCheckedIds.push(el.id);
	} else if (logicObj.apiCheckedIds.includes(el.id) && el.checked) {
		const idx = logicObj.apiCheckedIds.indexOf(el.id);
		logicObj.apiCheckedIds.splice(idx, 1);
	}
	if (!props.document) {
		logicObj.onSaveChanges();
	}
}
function checkedAllowance(el, logicObj) {
	clearErrorMessages();
	if (logicObj.apiCheckedLocationAllowanceIds.includes(el.id) && !el.checked)
		return;
	/**
	 * // TODO: refactor or recreate component EditDocumentDetails
	 * el.checked is always late due to created logic in EditDocumentDetails
	 * onClickInputRow() is called before checked is being set
	 * that's why logic is opposite
	 */ else if (
		!logicObj.apiCheckedLocationAllowanceIds.includes(el.id) &&
		!el.checked
	) {
		logicObj.apiCheckedLocationAllowanceIds.push(el.id);
	} else if (
		logicObj.apiCheckedLocationAllowanceIds.includes(el.id) &&
		el.checked
	) {
		const idx = logicObj.apiCheckedLocationAllowanceIds.indexOf(el.id);
		logicObj.apiCheckedLocationAllowanceIds.splice(idx, 1);
	}
	if (!props.document) {
		logicObj.onSaveChanges();
	}
}
function checkParent(val, logicObj) {
	clearErrorMessages();
	if (val && val.length > 0) {
		for (const el of val) {
			if (logicObj.apiCheckedIds.includes(el.id) && el.checked) continue;
			else if (logicObj.apiCheckedIds.includes(el.id) && !el.checked) {
				const idx = logicObj.apiCheckedIds.indexOf(el.id);
				logicObj.apiCheckedIds.splice(idx, 1);
				continue;
			} else if (!logicObj.apiCheckedIds.includes(el.id) && el.checked) {
				logicObj.apiCheckedIds.push(el.id);
			}
		}
	}
	if (!props.document) {
		logicObj.onSaveChanges();
	}
}

// remove start

async function removeDocumentConfirm() {
	openGlobalConfirmModal(
		{
			title: "Delete Document",
			text: `Are you sure you want to delete a document with id: ${props.document.id}?`,
			warning: true,
		},
		async () => {
			// delete document
			await store.deleteDocument(props.document.id);
			if (documentsErrors.value) {
				openErrorsModal({
					content: documentsErrors.value,
				});
				store.resetDocumentsErrors();
			} else {
				const documents = computed(() => store.getDocuments());
				const index = documents.value.findIndex(
					(d) =>
						d.id === props.document.id &&
						d?.temp?.id === props.document?.temp?.id,
				);
				if (index !== -1) documents.value.splice(index, 1);
				store.setDocuments(documents.value);
			}
		},
	);
}

// remove end

async function saveNewDocument(): Promise<void> {
	isLoadingSaveNewDocument.value = true;
	tempDocInfo.value.expiration_length = tempDocInfo.value.has_expiry
		? tempDocInfo.value.expiration_length
		: null;
	tempDocInfo.value.notification_deadline = tempDocInfo.value.has_expiry
		? tempDocInfo.value.notification_deadline
		: null;
	const res = await store.saveNewDocument(tempDocInfo.value);
	if (documentsErrors.value) {
		openErrorsModal({
			content: documentsErrors.value,
		});
		store.resetDocumentsErrors();
	} else {
		const newDocument = ref(res.data.data);
		newDocument.value.roles = tempDocInfo.value.roles;
		newDocument.value.locations = tempDocInfo.value.locations;
		newDocument.value.categories = tempDocInfo.value.categories;

		const docs = store.getDocuments();
		const insertIndex = docs.findIndex(
			(doc) =>
				doc.name.localeCompare(newDocument.value.name, "en", {
					sensitivity: "base",
				}) > 0,
		);
		docs.splice(insertIndex, 0, newDocument.value);
		store.setDocuments(docs);

		setTempDocumentInfo();

		toastStore.openToastSuccess("New document successfully created.");

		emit("close-modal");
	}
	isLoadingSaveNewDocument.value = false;
}

const isNewDocumentTypeFormValid = computed(() => {
	const dt = tempDocInfo.value;

	const hasName = dt.name.trim().length > 0;
	const hasCategories = dt.categories.length > 0;
	const hasLocations = dt.locations.length > 0;
	const hasRoles = dt.roles.length > 0;

	return hasName && hasCategories && hasLocations && hasRoles;
});

watch(
	() => props.document,
	() => {
		setTempDocumentInfo(props.document);
		allCategories.value = props.categories;
		allRoles.value = props.roles;
		allClients.value = props.clients;
	},
	{ immediate: true },
);
</script>

<template>
	<div id="edit-document-modal-wrap">
		<div class="edit-document-modal-section">
			<div
				class="section-header"
				@click="toggleSection('info')"
			>
				<i
					v-if="expandedSections.info"
					class="fa fa-caret-up"
				></i>
				<i
					v-else
					class="fa fa-caret-down"
				></i>
				<h2 class="mg-10 f-s-1-5">Info</h2>
			</div>
			<div
				v-if="expandedSections.info"
				class="section-content"
			>
				<div class="input-group">
					<label
						class="label"
						for="edit-document-name"
					>
						Name
					</label>
					<input
						id="edit-document-name"
						v-model="tempDocInfo.name"
						type="text"
						placeholder="Type in document name"
						class="input"
						:disabled="!canEdit"
						@input="clearErrorMessages()"
					/>
				</div>
				<div class="input-group-checkbox">
					<input
						id="edit-document-optional"
						v-model="isNotOptional"
						type="checkbox"
						class="input-checkbox"
						:disabled="!canEdit"
						@change="clearErrorMessages()"
					/>
					<label
						class="label"
						for="edit-document-optional"
					>
						Mandatory
					</label>
				</div>
				<div class="input-group">
					<label
						class="label"
						for="edit-document-info-link"
					>
						Info Link
					</label>
					<input
						id="edit-document-info-link"
						v-model="tempDocInfo.info_link"
						type="text"
						:placeholder="!canEdit ? '' : 'Edit Info Link'"
						class="input"
						:disabled="!canEdit"
						@input="clearErrorMessages()"
					/>
				</div>
				<div class="input-group-checkbox">
					<input
						id="edit-document-has-expiry"
						v-model="tempDocInfo.has_expiry"
						type="checkbox"
						class="input-checkbox"
						:disabled="!canEdit"
						@change="clearErrorMessages()"
					/>
					<label
						class="label"
						for="edit-document-has-expiry"
					>
						Has Expiry?
					</label>
				</div>
				<div
					v-if="tempDocInfo.has_expiry"
					class="input-group"
				>
					<label
						class="label"
						for="edit-document-expiration-length"
					>
						Automatic Expiry
					</label>
					<input
						id="edit-document-expiration-length"
						v-model="tempDocInfo.expiration_length"
						type="number"
						placeholder="(months)"
						class="input"
						min="1"
						:disabled="!canEdit"
						@input="clearErrorMessages()"
					/>
				</div>
				<div
					v-if="tempDocInfo.has_expiry"
					class="input-group"
				>
					<label
						class="label"
						for="edit-document-notification-deadline"
					>
						Deadline for Push/Email
					</label>
					<input
						id="edit-document-notification-deadline"
						v-model="tempDocInfo.notification_deadline"
						type="number"
						placeholder="(days)"
						class="input"
						min="1"
						:disabled="!canEdit"
						@input="clearErrorMessages()"
					/>
				</div>
				<button
					v-if="document && canEdit"
					class="btn"
					:disabled="!hasChangesInfo || isLoadingInfo"
					style="margin-top: 6px"
					@click="onClickEditInfoConfirm()"
				>
					Save
				</button>
				<div
					v-if="!updateDocumentInfoErrorMsg && document && canEdit"
					class="message"
				>
					{{ updateDocumentInfoMsg }}
				</div>
				<div
					v-else-if="updateDocumentInfoErrorMsg && document && canEdit"
					class="message error-message"
				>
					{{ updateDocumentInfoErrorMsg }}
				</div>
			</div>
		</div>

		<div class="edit-document-modal-section">
			<div
				class="section-header"
				@click="toggleSection('wards')"
			>
				<i
					v-if="expandedSections.wards"
					class="fa fa-caret-up"
				></i>
				<i
					v-else
					class="fa fa-caret-down"
				></i>
				<h2 class="mg-10 f-s-1-5">Applicable Wards</h2>
			</div>
			<div v-if="expandedSections.wards">
				<template v-if="wardsObj?.show && !wardsObj.isLoading">
					<edit-document-details
						:data="wardsObj?.data"
						:levels="wardsObj.levels"
						:isLoading="wardsObj?.isLoading"
						:isSaving="isSavingWards"
						:search="wardsObj.search"
						:checkbox="wardsObj.checkbox"
						:canViewAllowances="canViewAllowances"
						:displayCaret="wardsObj.displayCaret"
						:displayAllowance="false"
						:is-new-document="!Boolean(document)"
						:is-disabled="!canEdit"
						@edit-document="wardsObj.save"
						@update-search-value="editLocationsLogic.updateSearchValue"
						@select-all-checkbox="editLocationsLogic.selectAllCheckbox"
						@check-el="(e) => checkedEl(e, editLocationsLogic)"
						@check-parent="(e) => checkParent(e, editLocationsLogic)"
						@check-allowance="(e) => checkedAllowance(e, editLocationsLogic)"
						@select-all="editLocationsLogic.selectAll"
						@reset="
							() => {
								clearErrorMessages();
								editLocationsLogic.showLocations(tempDocInfo);
							}
						"
					></edit-document-details>
					<div
						v-if="!updateApplicableWardsErrorMsg && document && canEdit"
						class="message"
					>
						{{ updateApplicableWardsInfoMsg }}
					</div>
					<div
						v-else-if="updateApplicableWardsErrorMsg && document && canEdit"
						class="message error-message"
					>
						{{ updateApplicableWardsErrorMsg }}
					</div>
				</template>
				<div
					v-else
					class="loading-wrap"
				>
					<div class="loading-wrapper">
						<img
							class="client-loading"
							src="@/assets/image/loader/loader.svg"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="edit-document-modal-section">
			<div
				class="section-header"
				@click="toggleSection('categories')"
			>
				<i
					v-if="expandedSections.categories"
					class="fa fa-caret-up"
				></i>
				<i
					v-else
					class="fa fa-caret-down"
				></i>
				<h2 class="mg-10 f-s-1-5">Applicable Categories</h2>
			</div>
			<div v-if="expandedSections.categories">
				<template v-if="catObj?.show && !catObj.isLoading">
					<edit-document-details
						:data="catObj?.data"
						:levels="catObj.levels"
						:isLoading="catObj?.isLoading"
						:isSaving="isSavingCategories"
						:search="catObj.search"
						:checkbox="catObj.checkbox"
						:canViewAllowances="canViewAllowances"
						:displayCaret="catObj.displayCaret"
						:displayAllowance="false"
						:is-new-document="!Boolean(document)"
						:is-disabled="!canEdit"
						@edit-document="catObj.save"
						@update-search-value="editSubcategoriesLogic.updateSearchValue"
						@select-all-checkbox="editSubcategoriesLogic.selectAllCheckbox"
						@check-el="(e) => checkedEl(e, editSubcategoriesLogic)"
						@check-parent="(e) => checkParent(e, editSubcategoriesLogic)"
						@check-allowance="
							(e) => checkedAllowance(e, editSubcategoriesLogic)
						"
						@select-all="editSubcategoriesLogic.selectAll"
					></edit-document-details>
					<div
						v-if="!updateCategoriesErrorMsg && document && canEdit"
						class="message"
					>
						{{ updateCategoriesInfoMsg }}
					</div>
					<div
						v-else-if="updateCategoriesErrorMsg && document && canEdit"
						class="message error-message"
					>
						{{ updateCategoriesErrorMsg }}
					</div>
				</template>
				<div
					v-else
					class="loading-wrap"
				>
					<div class="loading-wrapper">
						<img
							class="client-loading"
							src="@/assets/image/loader/loader.svg"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="edit-document-modal-section">
			<div
				class="section-header"
				@click="toggleSection('visibility')"
			>
				<i
					v-if="expandedSections.visibility"
					class="fa fa-caret-up"
				></i>
				<i
					v-else
					class="fa fa-caret-down"
				></i>
				<h2 class="mg-10 f-s-1-5">Visibility</h2>
			</div>
			<div v-if="expandedSections.visibility">
				<template v-if="rolesObj?.show && !rolesObj.isLoading">
					<edit-document-details
						:data="rolesObj?.data"
						:levels="rolesObj.levels"
						:isLoading="rolesObj?.isLoading"
						:isSaving="isSavingRoles"
						:checkbox="rolesObj.checkbox"
						:canViewAllowances="canViewAllowances"
						:displayCaret="rolesObj.displayCaret"
						:displayAllowance="false"
						:is-new-document="!Boolean(document)"
						:is-disabled="!canEdit"
						style="height: unset"
						@edit-document="rolesObj.save"
						@check-el="(e) => checkedEl(e, editRolesLogic)"
						@check-parent="(e) => checkParent(e, editRolesLogic)"
						@check-allowance="(e) => checkedAllowance(e, editRolesLogic)"
					></edit-document-details>
					<div
						v-if="!updateRolesErrorMsg && canEdit"
						class="message"
					>
						{{ updateRolesInfoMsg }}
					</div>
					<div
						v-else-if="updateRolesErrorMsg && canEdit"
						class="message error-message"
					>
						{{ updateRolesErrorMsg }}
					</div>
				</template>
				<div
					v-else
					class="loading-wrap"
				>
					<div class="loading-wrapper">
						<img
							class="client-loading"
							src="@/assets/image/loader/loader.svg"
						/>
					</div>
				</div>
			</div>
		</div>
		<button
			v-if="document && canEdit"
			class="btn is-danger"
			style="margin-top: 12px"
			@click="removeDocumentConfirm()"
		>
			Remove
		</button>
		<button
			v-else-if="!document && !isNewDocumentTypeFormValid"
			v-tooltip:right.accent="
				'All required fields must be filled in: Name, Wards, Categories, and Visibility.'
			"
			class="btn"
			style="margin-top: 12px"
			:disabled="isLoadingSaveNewDocument || !isNewDocumentTypeFormValid"
			@click="saveNewDocument()"
		>
			Save
		</button>
		<button
			v-else-if="!document && isNewDocumentTypeFormValid"
			class="btn"
			style="margin-top: 12px"
			:disabled="isLoadingSaveNewDocument || !isNewDocumentTypeFormValid"
			@click="saveNewDocument()"
		>
			Save
		</button>
	</div>
</template>

<style lang="scss" scoped>
#edit-document-modal-wrap {
	padding: 0 20px 30px;
	overflow-y: auto;
	overflow-x: hidden;
	width: 800px;
	max-width: 90vw;
	height: 85vh;

	.edit-document-modal-section {
		.section-header {
			display: flex;
			align-items: center;
			cursor: pointer;

			.header-caret {
				display: flex;
				align-items: center;
			}
		}

		.input-group {
			display: flex;
			flex-direction: column;

			.label {
				padding-left: 5px;
				margin: 0;
			}

			.input {
				width: 100%;
			}

			&:not(:last-child) {
				margin-bottom: 0.75rem;
			}
		}

		.input-group-checkbox {
			display: flex;
			align-items: center;
			margin-bottom: 0.75rem;

			.label {
				margin: 0;
				padding-top: 2px;
				cursor: pointer;
			}

			.input-checkbox {
				border: 1px solid $border-color;
				height: 3em;
				width: 20px;
				opacity: 0.6;
				font-size: 15px;
				font-weight: 500;
				margin: 0 5px;
			}
		}

		.message {
			height: 23px;
			margin: 12px 0;
			padding-left: 5px;

			&.error-message {
				color: $error-color;
			}
		}
	}
}
</style>
